import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useTheme } from '../../contexts/ThemeContext';
import { pageAPI } from '../../lib/api';
import PushPrompt from '../PushPrompt';
import {
  LayoutDashboard, FileText, ChevronRight, ChevronDown, Plus, Search,
  Star, MoreHorizontal, Trash2, Settings, Users, Bell, Sun, Moon,
  LogOut, ArrowLeft, FolderKanban, ClipboardList, Calendar, CalendarClock, User,
  Sparkles, Shield, LayoutGrid, StickyNote, Building2, BarChart3,
  CalendarDays,
  Minimize2, Maximize2, X as XIcon, Loader2, Plug2, MessageSquare, Workflow,
  FileSignature, ClipboardCheck, Menu, Mic, Wallet, Lightbulb, Timer, MailCheck, Smartphone
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Separator } from '../ui/separator';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { StellarSelect } from '../StellarFormControls';
import { toast } from 'sonner';
import { notificationAPI, aiChatAPI, searchAPI } from '../../lib/api';

import useSidebarCounts, { DEFAULT_SIDEBAR_COUNTS } from '../../lib/useSidebarCounts';
import NotificationBell from '../NotificationBell';
import { StorageDegradedBanner } from '../StorageDegradedBanner';
import { ErrorBoundary } from '../ErrorBoundary';
import { usePushNotifications } from '../../lib/usePushNotifications';
import AttendanceWidget from '../attendance/AttendanceWidget';

const LOGO = '/stp-logo.png';

// Main navigation — new layout per user request
// Iter 61 (Session A Part 1): sidebar re-ordered + relabeled per Owner spec.
// Exact order: Dashboard → Tasks → My Clients → Team Chat → Stellar's AI →
// Deadline Calendar → Availability Calendar → Team EOD Report → My Notes →
// Automations → Integrations → Settings.
const MAIN_NAV = [
  { icon: LayoutDashboard, label: 'Dashboard',             path: '/dashboard' },
  { icon: ClipboardList,   label: 'Tasks',                 path: '/tasks/all', badgeKey: 'tasks' },
  { icon: Lightbulb,       label: 'Idea Bank',             path: '/tasks/idea-bank', badgeKey: 'idea_bank' },
  // Batch 5 D48 BUG-09 — Reviews queue. Badge wired below in renderer.
  { icon: ClipboardCheck,  label: 'Reviews',               path: '/reviews', badgeKey: 'reviews' },
  { icon: Building2,       label: 'My Clients',            path: '/tasks/clients', badgeKey: 'my_clients' },
  // 2026-07-15 — Team Chat, Stellar's AI + Meeting Notes nav entries HIDDEN
  // per owner directive (sidebar cleanup). Routes, pages and data remain
  // intact — only the nav items (and their unread badges) are gone.
  // { icon: MessageSquare,   label: 'Team Chat',             path: '/tasks/chat', badgeKey: 'team_chat' },
  // { icon: Sparkles,        label: "Stellar's AI",          path: '/tasks/ai-chat' },
  { icon: CalendarClock,   label: 'Deadline Calendar',     path: '/tasks/calendar', badgeKey: 'deadline_calendar' },
  { icon: CalendarDays,    label: 'Availability Calendar', path: '/tasks/availability', badgeKey: 'availability_calendar' },
  { icon: Timer,           label: 'Attendance',            path: '/tasks/attendance' },
  { icon: BarChart3,       label: 'Team EOD Report',       path: '/tasks/eod-report', hideForRoles: ['team', 'member'] },
  // 2026-07 — Team Report removed from the sidebar (owner directive).
  // { icon: MailCheck,       label: 'Team Report',           path: '/tasks/report', ownerOnly: true },
  // 2026-08 — PWA install + notifications guide (forwardable to the team).
  { icon: Smartphone,      label: 'Install App',           path: '/tasks/install-guide' },
  // D94 / 2026-02-25 — Attendance + Payroll nav items DELETED per user directive.
  // The pages remain on the backend so attendance can be rebuilt as a
  // separate phase, but they no longer appear in the sidebar nav.
  { icon: ClipboardList,   label: 'My EOD',                path: '/eod', hideForRoles: ['super_admin', 'owner'] },
  { icon: StickyNote,      label: 'My Notes',              path: '/tasks/notes' },
  // { icon: Mic,             label: 'Meeting Notes',         path: '/meeting-notes' },
];

const BOTTOM_NAV = [
  { icon: Workflow, label: 'Automations',  path: '/tasks/agents' },
  { icon: Plug2,    label: 'Integrations', path: '/tasks/integrations' },
  { icon: Settings, label: 'Settings',     path: '/tasks/settings' },
];

// Iter 69 — Contracts is owner-only and slotted just above Settings.
const CONTRACTS_NAV_ITEM = {
  icon: FileSignature, label: 'Contracts', path: '/tasks/contracts', badgeKey: 'contracts',
};

export default function TaskManagerShell() {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.innerWidth < 768) return true;
    return localStorage.getItem('stellar_sidebar_collapsed') === 'true';
  });
  const toggleSidebar = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('stellar_sidebar_collapsed', String(next));
  };
  const { user, logout } = useAuth();
  const { currentWorkspace, workspaces, switchWorkspace, workspaceRole } = useWorkspace();
  // 2026-06-16 — Web Push enrollment. One-time prompt on first login;
  // re-syncs the subscription with the backend on every fresh device.
  usePushNotifications({ userId: user?.id, workspaceId: currentWorkspace?.id, autoEnroll: true });

  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // D54 RD4 — Mobile drawer auto-close on route change + ESC key. Only
  // applies under md (<768px). Detected via window.matchMedia so the desktop
  // collapsed state isn't disturbed.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    if (mq.matches && !collapsed) {
      setCollapsed(true);
      try { localStorage.setItem('stellar_sidebar_collapsed', 'true'); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        const mq = window.matchMedia('(max-width: 767px)');
        if (mq.matches && !collapsed) {
          setCollapsed(true);
          try { localStorage.setItem('stellar_sidebar_collapsed', 'true'); } catch {}
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [collapsed]);

  // D53 RD4 — Swipe-left to close + focus trap when drawer is open on mobile.
  const sidebarRef = useRef(null);
  const hamburgerRef = useRef(null);
  const touchStartXRef = useRef(null);
  const lastFocusRef = useRef(null);
  // Swipe-left-to-close: if touchstart inside sidebar AND deltaX < -60px, close.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const el = sidebarRef.current;
    if (!el) return;
    const mq = window.matchMedia('(max-width: 767px)');
    if (!mq.matches || collapsed) return;
    const onTouchStart = (e) => {
      touchStartXRef.current = e.touches?.[0]?.clientX ?? null;
    };
    const onTouchMove = (e) => {
      if (touchStartXRef.current == null) return;
      const dx = (e.touches?.[0]?.clientX ?? 0) - touchStartXRef.current;
      if (dx < -60) {
        touchStartXRef.current = null;
        setCollapsed(true);
        try { localStorage.setItem('stellar_sidebar_collapsed', 'true'); } catch {}
      }
    };
    const onTouchEnd = () => { touchStartXRef.current = null; };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [collapsed]);
  // Focus trap: when drawer opens on mobile, focus the first focusable element
  // inside, trap Tab cycling, and restore focus to the hamburger on close.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    if (!mq.matches) return;
    const el = sidebarRef.current;
    if (!el) return;
    if (!collapsed) {
      lastFocusRef.current = document.activeElement;
      const firstFocusable = el.querySelector('button, a, input, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) firstFocusable.focus();
      const onTab = (e) => {
        if (e.key !== 'Tab') return;
        const focusables = Array.from(el.querySelectorAll('button, a, input, [tabindex]:not([tabindex="-1"])'))
          .filter(n => !n.disabled && n.offsetParent !== null);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
      el.addEventListener('keydown', onTab);
      return () => el.removeEventListener('keydown', onTab);
    } else if (lastFocusRef.current && typeof lastFocusRef.current.focus === 'function') {
      // Restore focus to whatever opened the drawer (typically the hamburger)
      try { lastFocusRef.current.focus(); } catch {}
      lastFocusRef.current = null;
    }
  }, [collapsed]);
  const isAdmin = ['owner', 'admin', 'manager', 'super_admin'].includes(user?.role || '');
  // Iter 69 — Owner-only Contracts visibility
  const isOwner = user?.role === 'super_admin' || workspaceRole === 'owner';
  const [pages, setPages] = useState([]);
  const [expandedSections, setExpandedSections] = useState({ favorites: true, pages: true });
  // D94 / 2026-02-25 — Attendance system (clock-in/clock-out/breaks) is
  // COMPLETELY REMOVED from the UI per user directive. All users now use
  // the Manual EOD modal flow only. No clock state, no break state, no
  // attendance polling lives in this shell anymore.
  const [notifications, setNotifications] = useState([]);
  const [showAIChat, setShowAIChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchTimer, setSearchTimerState] = useState(null);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFullscreen, setAiFullscreen] = useState(false);
  const [aiModel, setAiModel] = useState('openai/gpt-4o-mini');
  const [aiModels, setAiModels] = useState([]);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiCompareMode, setAiCompareMode] = useState(false);
  const [aiCompareModels, setAiCompareModels] = useState(['openai/gpt-4o-mini', 'anthropic/claude-3.5-sonnet', 'google/gemini-2.0-flash-001']);
  const [aiCompareResults, setAiCompareResults] = useState([]);
  // D94 / 2026-02-24 — Manual Daily Report modal (replaces attendance-
  // tied EOD; attendance UI is fully deleted).
  // Phase 3A Follow-up — chat unread badge on the Team Chat nav item.
  // Polls every 20s for the sum of unread DMs/channel messages so the
  // sidebar reflects mentions and new chats even when /tasks/chat isn't open.
  // Batch 2A CHAT-USER-002 / T-018 — also listens to the `chat-channel-read`
  // custom event dispatched by TeamChatPage when the user opens a channel,
  // so the badge clears immediately without waiting for the next 20s tick.
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  useEffect(() => {
    const wsId = currentWorkspace?.id;
    if (!wsId) return undefined;
    let cancelled = false;
    const tick = () => {
      import('../../lib/api').then(({ chatAPI }) => {
        chatAPI.channels(wsId).then((res) => {
          if (cancelled) return;
          const total = (res.data || []).reduce((acc, ch) => acc + (ch.unread_count || 0), 0);
          setChatUnreadCount(total);
        }).catch(() => {});
      });
    };
    tick();
    const id = setInterval(tick, 20000);
    const onChannelRead = () => tick();
    window.addEventListener('chat-channel-read', onChannelRead);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('chat-channel-read', onChannelRead);
    };
  }, [currentWorkspace?.id]);

  // Batch 5 D48 BUG-09 — Reviews badge count. Polls every 30s + refreshes
  // on global task-changed events (BUG-07).
  const [reviewsCount, setReviewsCount] = useState(0);

  // D63 Issue 5 — Unified sidebar count badges (live, 15s polling + custom-event refresh).
  const { counts: sidebarCounts = DEFAULT_SIDEBAR_COUNTS } = useSidebarCounts(currentWorkspace?.id);

  useEffect(() => {
    const wsId = currentWorkspace?.id;
    if (!wsId) { setReviewsCount(0); return undefined; }
    let cancelled = false;
    const tick = () => {
      import('../../lib/api').then(({ reviewsAPI }) => {
        reviewsAPI.count(wsId).then(res => {
          if (cancelled) return;
          setReviewsCount(res.data?.count || 0);
        }).catch(() => {});
      });
    };
    tick();
    const id = setInterval(tick, 30000);
    const onChanged = () => tick();
    window.addEventListener('stellar-task-changed', onChanged);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('stellar-task-changed', onChanged);
    };
  }, [currentWorkspace?.id]);

  const handleSearch = (query) => {
    if (searchTimer) clearTimeout(searchTimer);
    if (!query || query.length < 2 || !currentWorkspace?.id) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await searchAPI.search(currentWorkspace.id, query);
        setSearchResults(res.data.results || []);
      } catch { setSearchResults([]); }
    }, 300);
    setSearchTimerState(t);
  };

  // Close search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = () => { if (searchResults.length > 0) setSearchResults([]); };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [searchResults.length]);

  const fetchPages = useCallback(() => {
    if (!currentWorkspace?.id) return;
    pageAPI.list(currentWorkspace.id).then(res => setPages(res.data)).catch(() => {});
  }, [currentWorkspace?.id]);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  // Fetch notifications
  useEffect(() => {
    if (!currentWorkspace?.id) return;
    notificationAPI.list(20).then(res => setNotifications(res.data?.items || [])).catch(() => {});
    const interval = setInterval(() => {
      notificationAPI.list(10).then(res => setNotifications(res.data?.items || [])).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [currentWorkspace?.id]);

  const handleAIAsk = async (query) => {
    if (!currentWorkspace?.id || !query.trim()) return;
    setAiLoading(true);
    try {
      if (aiCompareMode) {
        setAiCompareResults([]);
        const res = await aiChatAPI.compare({ message: query, models: aiCompareModels, workspace_id: currentWorkspace.id });
        setAiCompareResults(res.data || []);
        setAiQuery('');
      } else {
        const res = await aiChatAPI.chat({ message: query, model: aiModel, workspace_id: currentWorkspace.id });
        setAiHistory(prev => [...prev, { role: 'user', text: query }, { role: 'ai', text: res.data.reply, model: res.data.model }]);
        setAiQuery('');
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'AI failed'); } finally { setAiLoading(false); }
  };

  // Fetch AI models on first open
  useEffect(() => {
    if (showAIChat && aiModels.length === 0) {
      aiChatAPI.models().then(res => {
        const popular = (res.data || []).filter(m => ['openai/gpt-4o-mini', 'openai/gpt-4o', 'anthropic/claude-3.5-sonnet', 'anthropic/claude-3-haiku', 'google/gemini-2.0-flash-001', 'meta-llama/llama-3.1-70b-instruct', 'mistralai/mistral-large'].some(p => m.id === p));
        setAiModels(popular.length > 0 ? popular : (res.data || []).slice(0, 15));
      }).catch(() => {});
      aiChatAPI.preferences().then(res => { if (res.data?.default_model) setAiModel(res.data.default_model); }).catch(() => {});
    }
  }, [showAIChat]);

  const createPage = async () => {
    if (!currentWorkspace?.id) return;
    try {
      const res = await pageAPI.create({ workspace_id: currentWorkspace.id, title: 'Untitled' });
      fetchPages();
      navigate(`/tasks/pages?id=${res.data.id}`);
    } catch { toast.error('Failed to create page'); }
  };

  const toggleFavorite = async (e, pageId, current) => {
    e.stopPropagation();
    try {
      await pageAPI.update(currentWorkspace.id, pageId, { is_favorite: !current });
      fetchPages();
    } catch {}
  };

  const deletePage = async (e, pageId) => {
    e.stopPropagation();
    try {
      await pageAPI.delete(currentWorkspace.id, pageId);
      fetchPages();
      toast.success('Page deleted');
    } catch {}
  };

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));

  const initials = (user?.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  // 2026-02-20 — Live avatar URL. Defaults to user.avatar_url; updates
  // optimistically when the user uploads a new photo via Settings
  // (`stellar-avatar-updated` event from ProfileAvatarSection).
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  useEffect(() => { setAvatarUrl(user?.avatar_url || ''); }, [user?.avatar_url]);
  useEffect(() => {
    const onUpd = (e) => { setAvatarUrl(e?.detail?.avatar_url || ''); };
    window.addEventListener('stellar-avatar-updated', onUpd);
    return () => window.removeEventListener('stellar-avatar-updated', onUpd);
  }, []);
  const favorites = pages.filter(p => p.is_favorite);
  const allPages = pages.filter(p => !p.parent_page_id);

  return (
    <div className="flex flex-col h-screen bg-background overflow-hidden">
      {/* Iter 64 — storage outage banner (toggled by Owner via workspace flag) */}
      <StorageDegradedBanner visible={!!currentWorkspace?.storage_degraded_banner} />
      <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* Mobile overlay */}
      {!collapsed && <div className="md:hidden mobile-sidebar-overlay" onClick={() => setCollapsed(true)} data-testid="mobile-sidebar-overlay" />}
      {/* Sidebar */}
      <aside ref={sidebarRef} id="mobile-sidebar" role="navigation" aria-label="Main navigation" aria-hidden={collapsed ? 'true' : 'false'} className={`${collapsed ? 'w-0 md:w-[56px] overflow-hidden' : 'w-[260px] fixed md:relative z-50 md:z-auto'} bg-[hsl(var(--card))] border-r border-border/50 flex flex-col transition-all duration-200 shrink-0 h-full overflow-x-hidden`} data-testid="mobile-sidebar">
        {/* Workspace header — clickable for switching */}
        <div className="h-12 flex items-center px-3 gap-2 shrink-0">
          {collapsed ? (
            /* Collapsed: just show initial + expand button */
            <button onClick={toggleSidebar} className="h-8 w-8 rounded-lg bg-gold/10 flex items-center justify-center mx-auto hover:bg-gold/20 transition-colors" title="Expand sidebar">
              <ChevronRight className="h-3.5 w-3.5 text-gold" />
            </button>
          ) : (<>
            <img src={LOGO} alt="S" className="h-5 w-5 object-contain rounded" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-[13px] font-semibold truncate flex-1 text-left hover:text-gold transition-colors" data-testid="workspace-switcher">
                  {currentWorkspace?.name || 'Stellar Productions'}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {(workspaces || []).map(ws => (
                  <DropdownMenuItem key={ws.id} onClick={() => switchWorkspace(ws)} className="text-[12px]">
                    {ws.name} {ws.id === currentWorkspace?.id && <span className="ml-auto text-gold text-[10px]">Current</span>}
                  </DropdownMenuItem>
                ))}
                {/* Phase 3B Batch 1 T-CRIT-1 — workspace creation is Owner-only. */}
                {(user?.role === 'super_admin' || workspaceRole === 'owner') && (
                  <DropdownMenuItem onClick={() => navigate('/tasks/settings?new=true')} className="text-[12px] text-gold" data-testid="ws-switcher-create-btn">
                    <Plus className="h-3 w-3 mr-1.5" /> Create Workspace
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" className="h-6 w-6 opacity-60 hover:opacity-100" onClick={toggleSidebar}>
              <ChevronRight className="h-3.5 w-3.5 rotate-180" />
            </Button>
          </>)}
        </div>

        {/* Search — Iter 61: shortened placeholder so it doesn't overflow
            the 260px sidebar input (was "Search tasks, projects, notes…"
            which got clipped). Full description shown via title tooltip. */}
        <div className={`px-3 pb-2 relative ${collapsed ? 'hidden' : ''}`}>
          <div className="flex items-center gap-2 h-[34px] px-2.5 rounded-md bg-transparent border border-border/20 focus-within:border-gold/30 transition-colors">
            <Search className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
            <input
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); handleSearch(e.target.value); }}
              onFocus={() => { if (searchQuery.length >= 2) handleSearch(searchQuery); }}
              placeholder="Search…"
              title="Search tasks, projects, and notes"
              className="bg-transparent border-none outline-none text-[12px] flex-1 min-w-0 placeholder:text-muted-foreground/40 focus:placeholder:text-gold/40"
              data-testid="global-search"
            />
            {searchQuery && <button onClick={() => { setSearchQuery(''); setSearchResults([]); }} className="text-muted-foreground/40 hover:text-muted-foreground/70"><XIcon className="h-3 w-3" /></button>}
          </div>
          {/* Results dropdown */}
          {(searchResults.length > 0 || (searchQuery.length >= 2 && searchResults.length === 0)) && (
            <div className="absolute left-3 right-3 top-full mt-1 bg-card border border-border/30 rounded-lg shadow-xl z-50 max-h-[280px] overflow-y-auto py-1">
              {searchResults.length === 0 ? (
                <p className="text-[11px] text-muted-foreground/40 text-center py-5">No results for "{searchQuery}"</p>
              ) : (
                <>
                  <p className="text-[9px] text-muted-foreground/30 uppercase tracking-wider px-3 py-1">{searchResults.length} results</p>
                  {searchResults.map(r => (
                    <button key={`${r.type}-${r.id}`} onClick={() => { navigate(r.link); setSearchQuery(''); setSearchResults([]); }} className="flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-foreground/[0.03] transition-colors">
                      <span className="text-[8px] px-1.5 py-0.5 rounded bg-foreground/[0.05] text-muted-foreground/40 uppercase tracking-wider shrink-0 min-w-[44px] text-center">{r.type}</span>
                      <span className="text-[12px] truncate">{r.title}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        <ScrollArea className="flex-1">
          {/* Fixed nav */}
          <div className="px-2 py-1">
            {MAIN_NAV.filter(item => !(item.hideForRoles || []).includes(user?.role) && (!item.ownerOnly || isOwner)).map(item => {
              // 2026-07 — Sidebar fix: "Tasks" stays highlighted on every
              // Tasks view (My Tasks / Team / By Client + legacy paths).
              const isActive = item.path === '/tasks/all'
                ? ['/tasks/all', '/tasks/my-tasks', '/tasks/team', '/tasks/by-client', '/tasks/by-status', '/tasks/following'].includes(location.pathname)
                : location.pathname === item.path;
              // D63 Issue 5 — every nav item with a `badgeKey` consults the
              // unified `/api/sidebar/counts` payload. Chat / Reviews fall back
              // to legacy local counts when sidebar counts haven't arrived yet
              // (initial paint), so existing behavior is preserved.
              const isChat = item.path === '/tasks/chat';
              const isReviews = item.badgeKey === 'reviews';
              const liveCount = item.badgeKey ? (sidebarCounts[item.badgeKey] || 0) : 0;
              const badgeCount = liveCount
                || (isChat ? chatUnreadCount : isReviews ? reviewsCount : 0);
              const showBadge = badgeCount > 0;
              const badgeLabel = badgeCount > 99 ? '99+' : String(badgeCount);
              // Color escalation:
              //   amber for due-soon items (My EOD, Tasks, Deadlines)
              //   emerald for positive (Reviews queue)
              //   rose for everything else (default actionable)
              const amberItems = ['my_eod', 'tasks', 'deadline_calendar'];
              const badgeBg = isReviews
                ? 'bg-amber-500'
                : item.badgeKey === 'idea_bank'
                  ? 'bg-gold/70'
                  : amberItems.includes(item.badgeKey)
                    ? 'bg-amber-500'
                    : 'bg-rose-500';
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    // D65 P0-3 — mark badge viewed so it clears on click.
                    // Fire-and-forget; backend invalidates cache so the next
                    // sidebar refresh decrements within 2s.
                    // Idea Bank count is informational, never snoozed.
                    if (item.badgeKey && showBadge && item.badgeKey !== 'idea_bank') {
                      try {
                        const token = localStorage.getItem('stellar_token');
                        const wsId = currentWorkspace?.id || '';
                        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sidebar/mark-viewed`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ badge_key: item.badgeKey, workspace_id: wsId }),
                        }).then(() => {
                          // Trigger the global sidebar counts refresh.
                          window.dispatchEvent(new Event('stellar-sidebar-counts-refresh'));
                        }).catch(() => {});
                      } catch {}
                    }
                    navigate(item.path);
                  }}
                  className={`flex items-center gap-2.5 w-full ${collapsed ? 'justify-center px-0 py-2 relative' : 'px-2 py-1.5'} rounded-md text-[13px] transition-colors ${
                    isActive
                      ? 'nav-active bg-gold/[0.12] text-gold font-bold'
                      : 'text-foreground hover:bg-foreground/[0.06]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={collapsed ? `${item.label}${showBadge ? ` (${badgeLabel})` : ''}` : undefined}
                >
                  <item.icon className={`h-4 w-4 shrink-0 ${isActive ? '' : 'opacity-80'}`} />
                  <span className={`truncate ${collapsed ? "hidden" : ""}`}>{item.label}</span>
                  {showBadge && (
                    <span
                      className={`${collapsed ? 'absolute top-0.5 right-0.5' : 'ml-auto'} inline-flex items-center justify-center h-4 min-w-[18px] rounded-full ${badgeBg} text-white text-[9px] font-bold px-1.5`}
                      data-testid={`nav-${item.badgeKey || (isChat ? 'chat-unread' : 'reviews')}-badge`}
                      title={`${badgeLabel} pending`}
                      aria-label={`${badgeLabel} pending in ${item.label}`}
                    >
                      {badgeLabel}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <Separator className={`mx-3 my-1.5 opacity-50 ${collapsed ? 'mx-1' : ''}`} />

          {/* Bottom nav */}
          <div className="px-2 py-1">
            {(() => {
              // Iter 69 — splice Contracts in just above Settings, owner-only.
              // Iter 71 — Automations + Integrations are admin-or-owner only.
              const isAdminOrOwner =
                user?.role === 'super_admin' ||
                workspaceRole === 'owner' ||
                workspaceRole === 'admin';
              let items = [...BOTTOM_NAV];
              if (!isAdminOrOwner) {
                items = items.filter(i => i.path !== '/tasks/agents' && i.path !== '/tasks/integrations');
              }
              if (isOwner) {
                const settingsIdx = items.findIndex(i => i.path === '/tasks/settings');
                if (settingsIdx >= 0) items.splice(settingsIdx, 0, CONTRACTS_NAV_ITEM);
                else items.push(CONTRACTS_NAV_ITEM);
              }
              return items;
            })().map(item => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-2.5 w-full ${collapsed ? 'justify-center px-0 py-2' : 'px-2 py-1.5'} rounded-md text-[13px] transition-colors ${
                    isActive
                      ? 'nav-active bg-gold/[0.12] text-gold font-bold'
                      : 'text-foreground hover:bg-foreground/[0.06]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon className={`h-4 w-4 shrink-0 ${isActive ? '' : 'opacity-80'}`} />
                  <span className={`truncate ${collapsed ? "hidden" : ""}`}>{item.label}</span>
                </button>
              );
            })}
            <button onClick={() => navigate('/')} className="hidden">Switch to CRM</button>
          </div>
        </ScrollArea>

        {/* 2026-02 — EOD V2: EOD is now captured by the Clock Out flow in
            AttendanceWidget. The old manual "Submit EOD Report" button was
            removed with the legacy EOD system. */}
        <div className="border-t border-border/50 p-2 space-y-1.5">
          <div className={`flex items-center gap-2 ${collapsed ? 'flex-col px-0 py-1' : 'px-2 py-1.5'}`}>
            <Avatar className="h-6 w-6">
              <AvatarFallback className="text-[9px] bg-secondary">{initials}</AvatarFallback>
            </Avatar>
            {!collapsed && <span className="text-[12px] truncate flex-1 text-foreground font-medium">{user?.name}</span>}
            {!collapsed && <Button variant="ghost" size="icon" className="h-6 w-6 text-foreground/80 hover:text-gold hover:bg-transparent" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
            </Button>}
            <Button variant="ghost" size="icon" className="h-6 w-6 text-foreground/80 hover:text-gold hover:bg-transparent" onClick={logout} title="Logout" data-testid="sidebar-logout-btn">
              <LogOut className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Iter 61 Part 4: persistent top bar with notification bell on
            every page. Iter 61 Part 4b (2026-05-04): added global "+ New Task"
            button so users can create tasks from anywhere (previously only
            inline on the dashboard + tasks pages). Mobile shows a hamburger +
            "Stellar" brand; desktop shows the New Task button + bell. */}
        <div className="h-11 border-b border-border/50 flex items-center px-4 shrink-0 justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Button
              ref={hamburgerRef}
              variant="ghost" size="icon"
              className="h-9 w-9 md:hidden"
              onClick={toggleSidebar}
              data-testid="mobile-sidebar-toggle"
              aria-label={collapsed ? 'Open menu' : 'Close menu'}
              aria-expanded={!collapsed}
              aria-controls="mobile-sidebar"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <span className="text-[13px] font-semibold md:hidden">Stellar</span>
          </div>
          <Button
            size="sm"
            onClick={() => navigate('/tasks/all?new=true')}
            className="h-8 bg-gold/10 hover:bg-gold/20 text-gold border border-gold/30 text-[12px] font-medium"
            data-testid="shell-new-task-btn"
            title="Create a new task (from any page)"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            <span className="hidden sm:inline">New Task</span>
          </Button>
          {/* 2026-02 — Attendance V2 clock in/break/out widget (rebuilt). */}
          <ErrorBoundary fallback={() => null}>
            <AttendanceWidget />
          </ErrorBoundary>
          <NotificationBell data-testid="shell-notification-bell" />
          {/* Batch 2B A15 (BUG-RW-D-019) — user avatar dropdown with theme
              toggle, Settings shortcut, and Logout in the top-right.
              Mirrors the sidebar foot theme toggle but in the spec'd
              location. Theme writes both ThemeContext and server prefs. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="h-8 w-8 rounded-full bg-gold/10 hover:bg-gold/20 flex items-center justify-center transition-colors overflow-hidden"
                aria-label="Open account menu"
                data-testid="shell-user-avatar"
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={user?.name || 'avatar'}
                    className="h-full w-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    data-testid="shell-user-avatar-img"
                  />
                ) : (
                  <span className="text-[11px] font-bold text-gold">{initials}</span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="shell-user-menu">
              <div className="px-2 py-2 border-b border-border/20">
                <p className="text-[12px] font-semibold text-foreground truncate">{user?.name}</p>
                <p className="text-[11px] text-muted-foreground/60 truncate">{user?.email}</p>
              </div>
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="shell-user-menu-settings">
                <Settings className="h-3.5 w-3.5 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                toggleTheme();
                // Server-side persist so the choice survives device hops.
                import('../../lib/api').then(({ authAPI }) => {
                  authAPI.patchPreferences({ theme: theme === 'dark' ? 'light' : 'dark' }).catch(() => {});
                });
              }} data-testid="shell-user-menu-theme">
                {theme === 'dark' ? <Sun className="h-3.5 w-3.5 mr-2" /> : <Moon className="h-3.5 w-3.5 mr-2" />}
                Switch to {theme === 'dark' ? 'Light' : 'Dark'} mode
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="shell-user-menu-logout">
                <LogOut className="h-3.5 w-3.5 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <main className="flex-1 overflow-y-auto relative pb-16 md:pb-0">
          {/* 2026-08 — mobile push opt-in banner (iOS install nudge / Enable) */}
          <PushPrompt />

          {/* D58 P0 HOTFIX — Per-page ErrorBoundary so any single page crash
              (e.g. a stale prod bundle hitting a missing route or a render
              error on /eod) degrades to a recoverable fallback INSIDE the
              shell, instead of blanking the entire window. The shell (nav,
              clock, logout) remains usable. */}
          <ErrorBoundary label="this page" data-testid="shell-outlet-error-boundary">
            <Outlet />
          </ErrorBoundary>

          {/* AI Chat Popup — hide on AI Chat page */}
          {showAIChat && location.pathname !== '/tasks/ai-chat' && (
            <div className={`fixed z-50 bg-card border border-border/50 shadow-2xl flex flex-col ${aiFullscreen ? 'inset-0' : aiCompareMode ? 'bottom-20 right-6 w-[720px] h-[560px] rounded-xl' : 'bottom-20 right-6 w-[400px] h-[520px] rounded-xl'}`} data-testid="ai-chat-popup">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border/30 shrink-0">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-gold" />
                  <span className="text-[13px] font-semibold">Stellar's AI</span>
                  {/* Mode toggle */}
                  <div className="flex items-center bg-secondary/30 rounded-md p-0.5 ml-2">
                    <button onClick={() => { setAiCompareMode(false); setAiCompareResults([]); }} className={`px-2 py-0.5 rounded text-[10px] transition-colors ${!aiCompareMode ? 'bg-gold/15 text-gold' : 'text-muted-foreground/50 hover:text-muted-foreground'}`} data-testid="ai-chat-tab">Chat</button>
                    <button onClick={() => setAiCompareMode(true)} className={`px-2 py-0.5 rounded text-[10px] transition-colors ${aiCompareMode ? 'bg-gold/15 text-gold' : 'text-muted-foreground/50 hover:text-muted-foreground'}`} data-testid="ai-compare-tab">Compare</button>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!aiCompareMode && (
                    <StellarSelect
                      value={aiModel}
                      onValueChange={(v) => { setAiModel(v); aiChatAPI.updatePreferences({ default_model: v }).catch(() => {}); }}
                      options={(aiModels.length > 0 ? aiModels : [{ id: aiModel, name: aiModel }]).map(m => ({ value: m.id, label: m.name || m.id }))}
                      size="sm"
                      className="h-6 max-w-[160px] text-[10px]"
                      testId="ai-model-select"
                    />
                  )}
                  <button onClick={() => setAiFullscreen(!aiFullscreen)} className="p-1 rounded hover:bg-foreground/[0.05]">{aiFullscreen ? <Minimize2 className="h-3.5 w-3.5 text-muted-foreground" /> : <Maximize2 className="h-3.5 w-3.5 text-muted-foreground" />}</button>
                  <button onClick={() => { setShowAIChat(false); setAiFullscreen(false); }} className="p-1 rounded hover:bg-foreground/[0.05]"><XIcon className="h-3.5 w-3.5 text-muted-foreground" /></button>
                </div>
              </div>

              {aiCompareMode ? (
                <>
                  {/* Compare mode: 3 model selectors */}
                  <div className="flex items-center gap-2 px-4 py-2 border-b border-border/20 shrink-0">
                    {[0, 1, 2].map(i => (
                      <StellarSelect
                        key={i}
                        value={aiCompareModels[i]}
                        onValueChange={(v) => { const n = [...aiCompareModels]; n[i] = v; setAiCompareModels(n); }}
                        options={(aiModels.length > 0 ? aiModels : [{ id: aiCompareModels[i], name: aiCompareModels[i] }]).map(m => ({ value: m.id, label: m.name || m.id }))}
                        size="sm"
                        className="flex-1 h-6 text-[10px]"
                        testId={`compare-model-${i}`}
                      />
                    ))}
                  </div>
                  {/* Compare results */}
                  <ScrollArea className="flex-1 p-3">
                    {aiCompareResults.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {aiCompareResults.map((r, i) => (
                          <div key={i} className="border border-border/20 rounded-lg p-2.5" data-testid={`compare-result-${i}`}>
                            <p className="text-[9px] text-gold/60 font-medium mb-1.5 truncate">{(r.model || '').split('/').pop()}</p>
                            <p className="text-[11px] leading-relaxed whitespace-pre-wrap text-muted-foreground">{r.reply}</p>
                          </div>
                        ))}
                      </div>
                    ) : !aiLoading ? (
                      <div className="flex flex-col items-center justify-center py-12">
                        <LayoutGrid className="h-8 w-8 text-muted-foreground/10 mb-3" />
                        <p className="text-[12px] text-muted-foreground/30">Compare 3 models side-by-side</p>
                        <p className="text-[10px] text-muted-foreground/20 mt-1">Ask a question below to see responses from all models</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-12 gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-gold/50" />
                        <span className="text-[12px] text-muted-foreground/40">Comparing models...</span>
                      </div>
                    )}
                  </ScrollArea>
                </>
              ) : (
                <>
                  {/* Chat mode (existing) */}
                  {aiHistory.length === 0 && (
                    <div className="p-3 space-y-1.5">
                      <p className="text-[11px] text-muted-foreground/50 px-1">Find, create, or ask anything</p>
                      {[{ label: 'Plan my day', q: 'Plan my day based on current tasks and priorities' }, { label: 'Break down a task', q: 'Help me break down a complex task into smaller steps' }, { label: 'Suggest priorities', q: 'Suggest priority ordering for my tasks' }, { label: 'Summarize status', q: 'Summarize current project status across all clients' }].map(a => (
                        <button key={a.label} onClick={() => { setAiQuery(a.q); handleAIAsk(a.q); }} className="w-full p-2 rounded-lg border border-border/40 text-[11px] text-left hover:bg-foreground/[0.02] hover:border-gold/20">{a.label}</button>
                      ))}
                    </div>
                  )}
                  <ScrollArea className="flex-1 p-3">
                    <div className="space-y-3">
                      {aiHistory.map((msg, i) => (
                        <div key={i} className={`${msg.role === 'user' ? 'text-right' : ''}`}>
                          {msg.role === 'user' ? (
                            <div className="inline-block max-w-[85%] p-2.5 rounded-lg bg-gold/10 text-left"><p className="text-[12px]">{msg.text}</p></div>
                          ) : (
                            <div className="max-w-[90%]">
                              <p className="text-[9px] text-muted-foreground/40 mb-0.5">{msg.model}</p>
                              <div className="p-2.5 rounded-lg bg-foreground/[0.03]"><p className="text-[12px] leading-relaxed whitespace-pre-wrap">{msg.text}</p></div>
                            </div>
                          )}
                        </div>
                      ))}
                      {aiLoading && <div className="flex items-center gap-2 text-[12px] text-muted-foreground p-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking...</div>}
                    </div>
                  </ScrollArea>
                </>
              )}

              {/* Input (shared between modes) */}
              <div className="p-3 border-t border-border/30 shrink-0">
                <div className="flex gap-2">
                  <input value={aiQuery} onChange={e => setAiQuery(e.target.value)} placeholder={aiCompareMode ? "Ask all 3 models..." : "Find, create, or ask anything..."} className="flex-1 h-8 px-3 rounded-md bg-secondary/50 border-none text-[12px] outline-none" onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) handleAIAsk(aiQuery); }} data-testid="ai-input" />
                  <Button size="sm" className="h-8" onClick={() => handleAIAsk(aiQuery)} disabled={aiLoading || !aiQuery.trim()} data-testid="ai-ask-btn">{aiCompareMode ? 'Compare' : 'Ask'}</Button>
                </div>
              </div>
            </div>
          )}

          {/* Sticky AI Button — hide on AI Chat page */}
          {location.pathname !== '/tasks/ai-chat' && (
            <button onClick={() => { setShowAIChat(!showAIChat); setAiResponse(''); setAiQuery(''); }} className="fixed bottom-20 right-6 z-40 h-12 w-12 rounded-full bg-card border border-gold/30 shadow-lg hover:shadow-xl hover:border-gold/50 flex items-center justify-center transition-all hover:scale-105 group" data-testid="sticky-ai-btn" title="AI Assistant">
              <Sparkles className="h-5 w-5 text-gold" />
            </button>
          )}
        </main>

        {/* 2026-08 — Mobile bottom nav (app-like): Dashboard / Tasks /
            Reviews / More. "More" opens the full sidebar menu. md+ hidden. */}
        <nav
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[hsl(var(--card))] border-t border-border/50 flex items-stretch justify-around pb-[env(safe-area-inset-bottom)]"
          data-testid="mobile-bottom-nav"
          aria-label="Mobile navigation"
        >
          {[
            { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
            { icon: ClipboardList, label: 'Tasks', path: '/tasks/all', match: ['/tasks/all', '/tasks/my-tasks', '/tasks/team', '/tasks/by-client', '/tasks/by-status', '/tasks/following'] },
            { icon: ClipboardCheck, label: 'Reviews', path: '/reviews' },
          ].map(item => {
            const active = item.match ? item.match.includes(location.pathname) : location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); if (!collapsed) setCollapsed(true); }}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 min-h-[52px] transition-colors ${active ? 'text-gold' : 'text-muted-foreground/50'}`}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}
          <button
            onClick={toggleSidebar}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 min-h-[52px] transition-colors ${!collapsed ? 'text-gold' : 'text-muted-foreground/50'}`}
            data-testid="bottom-nav-more"
            aria-label="More menu"
          >
            <Menu className="h-5 w-5" />
            <span className="text-[10px] font-medium">More</span>
          </button>
        </nav>
      </div>
      </div>


    </div>
  );
}

function PageSidebarItem({ page, navigate, location, onToggleFavorite, onDelete }) {
  const isActive = location.search.includes(page.id);
  const hasCustomIcon = page.icon && page.icon.length > 0;

  return (
    <div className={`group flex items-center gap-1 px-2 py-1 rounded-md text-[13px] cursor-pointer transition-colors ${isActive ? 'nav-active bg-foreground/[0.04] text-gold' : 'text-muted-foreground hover:bg-foreground/[0.03]'}`}>
      {hasCustomIcon ? <span className="text-[12px] w-4 text-center shrink-0">{page.icon}</span> : <FileText className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />}
      <span className="truncate flex-1" onClick={() => navigate(`/tasks/pages?id=${page.id}`)}>{page.title || 'Untitled'}</span>
      <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="h-5 w-5 flex items-center justify-center rounded hover:bg-secondary"><MoreHorizontal className="h-3 w-3" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-40">
            <DropdownMenuItem onClick={(e) => onToggleFavorite(e, page.id, page.is_favorite)}>
              <Star className="h-3.5 w-3.5 mr-2" /> {page.is_favorite ? 'Remove favorite' : 'Add to favorites'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => onDelete(e, page.id)} className="text-destructive">
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
